import { HTTPClient } from "@abaco/web-common/src/HTTP";
import {
    dbGIStoStandardPage,
    PagedResults,
    PagingParams,
    standardPagingParamToDbGIS
} from "@abaco/web-common/src/Paging";
import {
    BuyerContract,
    BuyerContractDetail,
    BuyerContractOut,
    BuyerContractUpdateOut,
    BuyerContractThresholdArea,
    BuyerContractReferencesUpdateOut,
    ThresholdBuyerContract,
    BuyerContractFilter,
    Profile,
    BuyerContractActivateOut,
    BuyerContractThresholdUser,
    BuyerContractWDetails,
    OrganisationThreshold
} from "../models/BuyerContract";
import axios, { CancelTokenSource } from "axios";
import SsoVuexModule from "@abaco/sso-web-module/src/abaco-module/state";
import { PricingProfile, ProfileUpgrade, ProfileUpgradeOut } from "../models/Pricing";
import { ProfileUser, ProfileUserOut } from "../models/User";

const RPP = 25;

export default class BuyerContractApi {
    private http: HTTPClient;
    private rootPath: string;
    private cancelTokenSource: CancelTokenSource | null;
    private ssoStore: SsoVuexModule;

    constructor(http: HTTPClient, rootPath: string, ssoStore: SsoVuexModule) {
        this.http = http;
        this.rootPath = rootPath;
        this.cancelTokenSource = null;
        this.ssoStore = ssoStore;
    }

    public async getBuyerContracts(
        paging: PagingParams,
        hasToken: boolean,
        filter?: string | null
    ): Promise<PagedResults<BuyerContract>> {
        if (hasToken && this.cancelTokenSource) {
            this.cancelTokenSource.cancel();
        }
        if (hasToken) {
            this.cancelTokenSource = axios.CancelToken.source();
        }
        const filterArr = [];
        if (filter) {
            filterArr.push(`contract_code like ${filter.toUpperCase()}`);
        }
        const today = new Date();
        filterArr.push(`dt_insert le ` + today.getTime());
        filterArr.push(`dt_delete gt ` + today.getTime());
        const res = await this.http.get(`${this.rootPath}/v1/bc/dictionary_contracts`, {
            params: {
                filter: filterArr.length > 0 ? filterArr : undefined,
                offset: paging.offset,
                count: paging.count
            },
            cancelToken: hasToken ? this.cancelTokenSource?.token : undefined
        });
        return res.data;
    }

    public async getBuyerContractsWDetails(
        paging: PagingParams,
        hasToken: boolean,
        filter?: string | null
    ): Promise<PagedResults<BuyerContractWDetails>> {
        if (hasToken && this.cancelTokenSource) {
            this.cancelTokenSource.cancel();
        }
        if (hasToken) {
            this.cancelTokenSource = axios.CancelToken.source();
        }
        const filterArr = [];
        if (filter) {
            filterArr.push(`contract_code like ${filter.toUpperCase()}`);
        }
        // const today = new Date();
        // filterArr.push(`dt_insert le ` + today.getTime());
        // filterArr.push(`dt_delete gt ` + today.getTime());
        const res = await this.http.get(`${this.rootPath}/v1/bc/dashboard/usage`, {
            params: {
                filter: filterArr.length > 0 ? filterArr : undefined,
                offset: paging.offset,
                count: paging.count
            },
            cancelToken: hasToken ? this.cancelTokenSource?.token : undefined
        });
        return res.data;
    }

    public async getAllBuyerContractsWDetails(): Promise<BuyerContractWDetails[]> {
        let count = 0;
        let res: any[] = [];
        let totalcount = 0;
        do {
            const curPage = await this.getBuyerContractsWDetails({ offset: count, count: 300 }, false, null);
            count += curPage.count;
            totalcount = curPage.totalcount;
            res = [...res, ...curPage.records];
        } while (count < totalcount);

        res = res.map(e => {
            return {
                ...e,
                ha_pagato: e.ha_pagato === "Si" ? true : false,
                data_1_sum: e.aziende + e.appezzamenti + e.lavoratori,
                data_2_sum: e.fabbricati + e.macchinari + e.movimentazione_magazzini
            };
        });
        return res;
    }

    public async getBuyerContractsSelf(userId: string): Promise<ThresholdBuyerContract[]> {
        const res = await this.http.get(
            `${this.rootPath}/v1/bc_business/contracts/getAllContractsForAdminUser/${userId}`,
            {
                params: {
                    offset: 0,
                    count: 300
                }
            }
        );
        return res.data.contract;
    }

    public async getBuyerContract(buyerContractId: number | undefined): Promise<BuyerContractDetail> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/bc_business/contracts/${buyerContractId}`);
        return res.data;
    }

    public async insertBuyerContract(detailOut: BuyerContractOut): Promise<any> {
        const res = await this.http.post(`/sitiagri-rest-api/api_basic/v1/bc_business/contracts/register`, detailOut);
        return res.data;
    }
    public async editBuyerContract(
        buyerContractId: number | undefined,
        detailOut: BuyerContractUpdateOut
    ): Promise<BuyerContract> {
        const res = await this.http.put(
            `/sitiagri-rest-api/api_basic/v1/bc_business/contracts/${buyerContractId}`,
            detailOut
        );
        return res.data;
    }
    public async editBuyerContractReferences(
        buyerContractId: number | undefined,
        detailOut: BuyerContractReferencesUpdateOut
    ): Promise<BuyerContract> {
        const res = await this.http.put(
            `/sitiagri-rest-api/api_basic/v1/bc_business/contracts/${buyerContractId}/references`,
            detailOut
        );
        return res.data;
    }
    public async deleteBuyerContract(buyerContractId: number): Promise<BuyerContract> {
        const res = await this.http.delete(
            `/sitiagri-rest-api/api_basic/v1/bc/dictionary_contracts/${buyerContractId}`
        );
        return res.data;
    }

    public async activateBuyerContract(
        buyerContractId: number | undefined,
        detailOut: BuyerContractActivateOut
    ): Promise<BuyerContract> {
        const res = await this.http.put(
            `/sitiagri-rest-api/api_basic/v1/bc_business/contracts/${buyerContractId}/activate`,
            detailOut
        );
        return res.data;
    }

    public async getProfiles(
        paging: PagingParams,
        filter: string | null,
        searchBy?: string
    ): Promise<PagedResults<Profile>> {
        const res = await this.http.get(`${this.rootPath}/v1/bc/dictionary_profiles`, {
            params: {
                offset: paging.offset,
                count: paging.count,
                filter: filter ? `^profile_id like ${filter.toUpperCase()}` : undefined
            }
        });
        return res.data;
    }

    public async getProfile(profileId: string): Promise<Profile> {
        const res = await this.http.get(`${this.rootPath}/v1/bc/dictionary_profiles/${profileId}`);
        return res.data;
    }

    public async getPartyCouncils(search: string | null, pagingParams: PagingParams): Promise<PagedResults<any>> {
        const params = standardPagingParamToDbGIS(pagingParams, pagingParams.count);
        const res = await this.http.get("/siticatasto/dbgis-servlet/tl_data/conduz/list_comu", {
            params: {
                search_str: search ? search : undefined,
                offset: params.offset,
                count: params.count
            }
        });
        const ret = dbGIStoStandardPage<any>(res.data, pagingParams.count);
        return ret;
    }

    public async checkFiscalCode(fiscalCode: string): Promise<string> {
        const res = await this.http.get("/siticatasto/dbgis-servlet/rest_tl_data", {
            params: {
                svccode: "portal/check_cf",
                codi_fisc: fiscalCode,
                lang: this.ssoStore.user?.locale
            }
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ret = dbGIStoStandardPage<any>(res.data, RPP).records[0].error as string;
        return ret;
    }

    public async checkVatNumber(vatNumber: string): Promise<string> {
        const res = await this.http.get("/siticatasto/dbgis-servlet/rest_tl_data", {
            params: {
                svccode: "portal/check_piva",
                codi_piva: vatNumber,
                lang: this.ssoStore.user?.locale
            }
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ret = dbGIStoStandardPage<any>(res.data, RPP).records[0].error;
        return ret;
    }

    public async getThresholdAreas(buyerContractId: number): Promise<BuyerContractThresholdArea[]> {
        const res = await this.http.get(`${this.rootPath}/v1/bc_business/contracts/${buyerContractId}/verifyUsedAreas`);
        //FOR TESTING
        // const res = await this.http.get(`/sitiagri-rest-api/api/v1/bc_business/contracts/${buyerContractId}/verifyUsedAreas`);
        return res.data.areas;
    }

    public async getThresholdUsers(buyerContractId: number): Promise<BuyerContractThresholdUser> {
        const res = await this.http.get(`${this.rootPath}/v1/bc_business/contracts/${buyerContractId}/verifyRelatedUsers`);
        //FOR TESTING
        //const res = await this.http.get(`/sitiagri-rest-api/api/v1/bc_business/contracts/${buyerContractId}/verifyRelatedUsers`);
        return res.data;
    }

    public async getThresholdOrganisations(buyerContractId: number): Promise<OrganisationThreshold> {
        const res = await this.http.get(`${this.rootPath}/v1/bc_business/contracts/${buyerContractId}/verifyOrganisations`);
        //FOR TESTING
        //const res = await this.http.get(`/sitiagri-rest-api/api/v1/bc_business/contracts/${buyerContractId}/verifyOrganisations`);
        return res.data;
    }

    public async getPricingPlan(): Promise<PricingProfile[]> {
        const res = await this.http.get(`${this.rootPath}/v1/bc/dictionary_profiles`);
        return res.data && res.data.records && res.data.records.length > 0 ? res.data.records : [];
    }

    public async getPricingPlanUpgrade(buyerContractId: number, detailOut: ProfileUpgradeOut): Promise<ProfileUpgrade> {
        const res = await this.http.put(
            `${this.rootPath}/v1/bc/dictionary_contracts/${buyerContractId}/payment`,
            detailOut
        );
        return res.data;
    }

    public async getProfileUsers(contractId: number): Promise<ProfileUser[]> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/bc_business/contracts/${contractId}/users`, {
        //FOR TESTING
        //const res = await this.http.get(`/sitiagri-rest-api/api/v1/bc_business/contracts/${contractId}/users`, {
            params: {
                offset: 0,
                count: 300
            }
        });
        return res.data;
    }

    public async addUser(contractId: number, detailForm: ProfileUserOut) {
        const form = {
            user: {
                isUserAnAdmin: false,
                user: {
                    userid: detailForm.userid,
                    user_name: detailForm.user_name
                }
            },
            associateToCompanies: true
        };
        const res = await this.http.post(`/sitiagri-rest-api/api_basic/v1/bc_business/contracts/${contractId}/createUser`, form);
        //FOR TESTING
        //const res = await this.http.post(`/sitiagri-rest-api/api/v1/bc_business/contracts/${contractId}/createUser`, form);
        return res.data;
    }

    public async existsSsoUser(username: string): Promise<boolean> {
        const url = "/siticatasto/dbgis-servlet/tl_data";
        const params = {
            svccode: "gurl_services/exists_sso_user",
            userid: username
        };

        const res = await this.http.get(url, { params });
        return res.data.services[0].entities[0].retcount > 0;
    }

    public async existsGuAccreditamento(username: string): Promise<boolean> {
        const url = "/siticatasto/dbgis-servlet/tl_data";
        const params = {
            svccode: "gurl_services/exists_gu_accreditamento",
            userid: username
        };

        const res = await this.http.get(url, { params });
        return res.data.services[0].entities[0].retcount > 0;
    }
}
